import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Heartbeat, Warning, CheckCircle, Info, ShieldCheck } from "@phosphor-icons/react";
import { api } from "../lib/api";

const LEVEL_STYLES = {
  critical: "border-rose-200 bg-rose-50 text-rose-800",
  warn: "border-amber-200 bg-amber-50 text-amber-800",
  info: "border-sky-200 bg-sky-50 text-sky-800",
};

const StatCard = ({ label, value, sub, tone }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={`health-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${tone || "text-zinc-900"}`}>{value}</div>
    {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

const recStatus = (s) => {
  const ok = ["verified"].includes((s || "").toLowerCase());
  const pending = ["pending", "not_started"].includes((s || "").toLowerCase());
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : pending ? "border-amber-200 bg-amber-50 text-amber-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
      {s || "unknown"}
    </span>
  );
};

export default function SenderHealthPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [domain, setDomain] = useState(null);

  useEffect(() => {
    setData(null);
    api.senderHealth(days).then(setData).catch(() => setData({ error: true }));
  }, [days]);
  useEffect(() => { api.domainHealth().then(setDomain).catch(() => setDomain({ error: true })); }, []);

  const t = data?.totals;

  return (
    <div className="space-y-5" data-testid="sender-health-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-xl font-semibold flex items-center gap-2"><Heartbeat size={22} className="text-rose-500" /> Sender health</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Your domain reputation at a glance — delivery, bounces, complaints and where your emails actually land.</p>
        </div>
        <div className="flex gap-1" data-testid="health-window">
          {[7, 30, 90].map(d => (
            <button key={d} data-testid={`health-window-${d}`} onClick={() => setDays(d)}
              className={`text-xs px-3 py-1.5 rounded-md border ${days === d ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200 hover:bg-zinc-50"}`}>
              {d} days
            </button>
          ))}
        </div>
      </div>

      {data?.error && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" data-testid="health-load-error">
          <Warning size={16} weight="fill" className="shrink-0" /> Could not load sender health data — please refresh. If this persists, the stats service needs attention.
        </div>
      )}

      {data?.alerts?.length > 0 && (
        <div className="space-y-2" data-testid="health-alerts">
          {data.alerts.map((a, i) => (
            <div key={i} className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${LEVEL_STYLES[a.level] || LEVEL_STYLES.info}`}>
              {a.level === "info" ? <Info size={16} className="mt-0.5 shrink-0" /> : <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />}
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}
      {data && !data.error && (!data.alerts || data.alerts.length === 0) && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800" data-testid="health-all-clear">
          <CheckCircle size={16} weight="fill" /> No reputation warnings in the last {days} days.
        </div>
      )}

      {t && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Sent" value={t.sent.toLocaleString()} />
          <StatCard label="Delivered" value={t.delivered_rate == null ? "—" : `${t.delivered_rate}%`} sub={t.delivered_rate == null ? "needs webhook" : `${t.delivered.toLocaleString()} confirmed`} />
          <StatCard label="Bounce rate" value={`${t.bounce_rate}%`} tone={t.bounce_rate >= 5 ? "text-rose-600" : t.bounce_rate >= 4 ? "text-amber-600" : "text-emerald-600"} sub="keep under 4%" />
          <StatCard label="Complaints" value={`${t.complaint_rate}%`} tone={t.complaint_rate >= 0.3 ? "text-rose-600" : t.complaint_rate >= 0.1 ? "text-amber-600" : "text-emerald-600"} sub="keep under 0.1%" />
          <StatCard label="Open rate" value={`${t.open_rate}%`} sub="pixel-based, directional" />
          <StatCard label="Click rate" value={`${t.click_rate}%`} />
        </div>
      )}

      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <div className="text-sm font-medium mb-3">Daily activity</div>
        {data?.daily?.length ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="sent" stroke="#18181b" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="delivered" stroke="#10b981" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="opened" stroke="#6366f1" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="clicked" stroke="#f59e0b" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="bounced" stroke="#e11d48" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-sm text-zinc-500 py-10 text-center">{data ? "No email activity in this window yet." : "Loading…"}</div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100 text-sm font-medium">Where your emails land — by provider</div>
          <table className="w-full text-sm" data-testid="provider-table">
            <thead className="text-xs text-zinc-500 border-b border-zinc-100"><tr>
              <th className="text-left px-3 py-2 font-medium">Provider</th>
              <th className="text-right px-3 py-2 font-medium">Sent to</th>
              <th className="text-right px-3 py-2 font-medium">Delivered</th>
              <th className="text-right px-3 py-2 font-medium">Opens</th>
              <th className="text-right px-3 py-2 font-medium">Clicks</th>
              <th className="text-right px-3 py-2 font-medium">Bounces</th>
            </tr></thead>
            <tbody>
              {(data?.providers || []).map(p => (
                <tr key={p.provider} className="border-b border-zinc-50">
                  <td className="px-3 py-2 font-medium">{p.provider}</td>
                  <td className="px-3 py-2 text-right">{p.sent.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-zinc-600">{p.delivered_rate}%</td>
                  <td className={`px-3 py-2 text-right ${data?.totals?.open_rate > 0 && p.sent >= 20 && p.open_rate < data.totals.open_rate * 0.4 ? "text-amber-600 font-semibold" : "text-zinc-600"}`}>{p.open_rate}%</td>
                  <td className="px-3 py-2 text-right text-zinc-600">{p.click_rate}%</td>
                  <td className={`px-3 py-2 text-right ${p.bounce_rate >= 5 ? "text-rose-600 font-semibold" : "text-zinc-600"}`}>{p.bounce_rate}%</td>
                </tr>
              ))}
              {(!data?.providers || data.providers.length === 0) && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-zinc-500">No recipient data in this window yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="domain-auth-card">
          <div className="flex items-center gap-2 text-sm font-medium mb-1"><ShieldCheck size={16} className="text-emerald-600" /> Domain authentication</div>
          <p className="text-[11px] text-zinc-500 mb-3">SPF and DKIM prove you're allowed to send from your domain; DMARC tells providers what to do with fakes. All three healthy = the strongest deliverability foundation.</p>
          {domain === null && <div className="text-sm text-zinc-400 py-4">Checking…</div>}
          {domain?.resend && (
            <div className="space-y-1.5 mb-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{domain.resend.domain}</span>
                {recStatus(domain.resend.status)}
              </div>
              {(domain.resend.records || []).map((r, i) => (
                <div key={i} className="flex items-center justify-between text-xs text-zinc-600 pl-2">
                  <span className="font-mono truncate mr-2">{r.record} · {r.name || r.type}</span>
                  {recStatus(r.status)}
                </div>
              ))}
            </div>
          )}
          {domain && !domain.resend && !domain.error && (
            domain.resend_error === "restricted_key"
              ? <p className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-2.5 py-1.5 mb-3">Your Resend API key is send-only, so SPF/DKIM status can't be read here. To see it, create a key with full access in Resend → API Keys. Your DMARC record below is checked directly via DNS.</p>
              : <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mb-3">Couldn't read domain status from Resend — check the sending domain in your Resend dashboard.</p>
          )}
          {domain?.dmarc && (
            <div className="flex items-center justify-between text-sm border-t border-zinc-100 pt-2.5">
              <div>
                <div className="font-medium">DMARC {domain.dmarc.found ? `(_dmarc.${domain.dmarc.domain})` : ""}</div>
                {domain.dmarc.found
                  ? <div className="text-[11px] text-zinc-500">policy: {domain.dmarc.policy || "?"}{domain.dmarc.policy === "none" ? " — consider moving to quarantine once stable" : ""}</div>
                  : <div className="text-[11px] text-zinc-500">No DMARC record found — add one (start with p=none) to strengthen trust with Gmail and Microsoft.</div>}
              </div>
              {domain.dmarc.found
                ? <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">found</span>
                : <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700">missing</span>}
            </div>
          )}
        </div>
      </div>

      <p className="text-[11px] text-zinc-400">Delivered, bounce and complaint data come from the Resend webhook. Opens are pixel-based and under-count when images are blocked — treat them as a trend. Scanner (bot) activity is excluded from everything on this page.</p>
    </div>
  );
}
